import React, { useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Textbox from 'carbon-react/lib/components/textbox';
import DateInput, { DateChangeEvent } from 'carbon-react/lib/components/date';
import { FilterableSelect, Option } from 'carbon-react/lib/components/select';

import { forceLocalDate } from '@futrli/predict/utils/dates';

import { useConsolidationContext } from '../../../consolidation-context/consolidation-context';
import { ExtendedCurrency } from '../../../consolidation-context/use-consolidation-crud/types';
import styles from './settings-form.scss';

export const SettingsForm = () => {
  const {
    currencies,
    handleSubmit,
    settings: { handleChangeCurrency, settingsForm }
  } = useConsolidationContext();
  const intl = useIntl();

  const onCurrenciesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!currencies) {
        return;
      }
      const currency = currencies.find(c => c.code === event.target.value);
      handleChangeCurrency(currency || null);
    },
    [currencies, handleChangeCurrency]
  );

  return (
    <form onSubmit={handleSubmit} data-testid="SettingsForm">
      <div className={styles.inputFieldContainer}>
        <Controller
          name="name"
          data-testid="SettingsForm-name"
          control={settingsForm.control}
          rules={{
            required: intl.formatMessage({
              id: 'common.please_enter_name',
              defaultMessage: 'Please enter a name'
            })
          }}
          render={({ field }) => (
            <Textbox
              {...field}
              id="name"
              size="medium"
              error={settingsForm.formState.errors?.name ? settingsForm.formState.errors?.name?.message : undefined}
              maxLength={30}
              label={intl.formatMessage({
                id: 'consolidation.consolidation_name',
                defaultMessage: 'Consolidation name'
              })}
              placeholder={intl.formatMessage({
                id: 'common.enter_name_here',
                defaultMessage: 'Enter name here'
              })}
              autoComplete="off"
              // disabled={isDisabled}
            />
          )}
        />
      </div>

      <div className={styles.inputFieldContainer}>
        <Controller
          name="yearEnd"
          control={settingsForm.control}
          rules={{
            required: intl.formatMessage({
              id: 'common.select_date',
              defaultMessage: 'Please select a date'
            })
          }}
          render={({ field }) => {
            const handleCarbonDateChange = (e: DateChangeEvent) => {
              field.onChange(forceLocalDate(e.target.value.rawValue || ''));
            };
            return (
              <div className={styles.carbonDate}>
                <DateInput
                  {...field}
                  onChange={handleCarbonDateChange}
                  id="yearEnd"
                  data-testid="SettingsForm-yearEnd"
                  label={intl.formatMessage({
                    id: 'consolidation.financial_year_end',
                    defaultMessage: 'Financial year end'
                  })}
                  size="medium"
                  maxWidth="100%"
                  error={
                    settingsForm.formState.errors?.yearEnd ? settingsForm.formState.errors?.yearEnd?.message : undefined
                  }
                  value={field.value ? intl.formatDate(new Date(field.value)) : ''}
                />
              </div>
            );
          }}
        />
      </div>

      <div className={styles.inputFieldContainer}>
        <Controller
          name="currency"
          control={settingsForm.control}
          rules={{
            required: intl.formatMessage({
              id: 'consolidation.please_enter_presentation_currency',
              defaultMessage: 'Please select a presentation currency'
            })
          }}
          render={({ field }) => (
            <FilterableSelect
              {...field}
              key="currency"
              label={intl.formatMessage({
                id: 'consolidation.presentation_currency',
                defaultMessage: 'Presentation currency'
              })}
              placeholder={intl.formatMessage({
                id: 'common.predict.please_select',
                defaultMessage: 'Please select...'
              })}
              value={field.value || ''}
              onChange={onCurrenciesChange}
              data-testid="SettingsForm-currency"
              error={
                settingsForm.formState.errors?.currency ? settingsForm.formState.errors?.currency?.message : undefined
              }
              listMaxHeight={560}
            >
              {currencies?.map((o: ExtendedCurrency, i: number) => (
                <Option key={`${o.code}-${i}`} value={o.code} text={o.displayName} disabled={o.code === '-'} />
              ))}
            </FilterableSelect>
          )}
        />
      </div>
    </form>
  );
};
